import { useEffectEvent, useLayoutEffect, useSyncExternalStore } from 'react';

type Close = () => void;

// The open panels whose open flag is a component's own state, each as the way it closes, in the order they
// opened. Module state, so a page deep in the tree reaches the rule the layout runs without a provider.
let openPanels: readonly Close[] = [];
const listeners = new Set<() => void>();

function setOpenPanels(next: readonly Close[]) {
    openPanels = next;
    listeners.forEach((notify) => notify());
}

function subscribe(notify: () => void) {
    listeners.add(notify);
    return () => {
        listeners.delete(notify);
    };
}

/** The joined panels open now, each as its close. */
export const useJoinedPanels = () => useSyncExternalStore(subscribe, () => openPanels);

/**
 * Joins a panel that keeps its open flag in local state to the new design's one-at-a-time rule: opening it
 * closes the other open panels, and another panel opening calls `close`, the panel's own. Only the new layout
 * reads the list, so in classic this changes nothing.
 */
export function useJoinedPanel(isOpen: boolean, close: Close) {
    const closeNow = useEffectEvent(close);

    // A layout effect, as the rule's own: it hears of the opening before two open panels are painted.
    useLayoutEffect(() => {
        if (!isOpen) return undefined;
        const entry = () => closeNow();
        setOpenPanels([...openPanels, entry]);
        return () => setOpenPanels(openPanels.filter((other) => other !== entry));
    }, [isOpen]);
}
